import type { SupabaseClient } from "@supabase/supabase-js";
import type { InsertProductBalanceHistory } from "~/types/database.types";
import type { Database, Json } from "~/types/supabase.types";
import type { ProductBalanceHistory } from "~/types/global.types";

type SupabaseProductBalanceHistoryRpc = SupabaseClient<Database> & {
  rpc: (
    name: "get_product_balance_history_daily"
  ) => Promise<{ data: ProductBalanceHistory[] | null; error: { message?: string } | null }>;
};

type ProductBalanceHistoryRow =
  Database["public"]["Tables"]["product_balance_history"]["Row"];
type ProductBalanceHistoryInput = ProductBalanceHistory | ProductBalanceHistoryRow;

function stableJsonStringify(value: unknown): string {
  if (value == null || typeof value !== "object") {
    return JSON.stringify(value) ?? "undefined";
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJsonStringify(item)).join(",")}]`;
  }

  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJsonStringify(record[key])}`)
    .join(",")}}`;
}

function calendarDayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function numericFullDataSignature(fullData: Json | null): string {
  if (fullData == null || typeof fullData !== "object" || Array.isArray(fullData)) {
    return "{}";
  }

  const numericValues: Record<string, number> = {};
  for (const [key, value] of Object.entries(fullData)) {
    if (typeof value === "number" && !Number.isNaN(value)) {
      numericValues[key] = value;
    }
  }

  return stableJsonStringify(numericValues);
}

function snapshotSignature(row: ProductBalanceHistoryInput): string {
  return JSON.stringify({
    assets: row.assets ?? 0,
    debts: row.debts ?? 0,
    liquid_assets: row.liquid_assets ?? 0,
    auto_imported_assets: row.auto_imported_assets ?? 0,
    numeric_full_data: numericFullDataSignature(row.full_data ?? null),
  });
}

/** Sum numeric `full_data` snapshots (from calculateBalances) for household merge. */
function mergeFullDataSnapshots(parts: (Json | null)[]): Json | null {
  const objects = parts.filter(
    (p) => p != null && typeof p === "object" && !Array.isArray(p)
  ) as Record<string, unknown>[];
  if (objects.length === 0) return null;
  if (objects.length === 1) return objects[0] as Json;

  const out: Record<string, unknown> = {};
  const keys = new Set<string>();
  for (const o of objects) {
    for (const k of Object.keys(o)) keys.add(k);
  }
  for (const key of keys) {
    if (key === "lastUpdate") {
      const merged: Record<string, string | undefined> = {};
      for (const o of objects) {
        const lu = o[key] as Record<string, string | undefined> | undefined;
        if (!lu || typeof lu !== "object") continue;
        for (const [k, v] of Object.entries(lu)) {
          if (v && (!merged[k] || v > merged[k]!)) merged[k] = v;
        }
      }
      if (Object.keys(merged).length) out[key] = merged;
      continue;
    }
    if (key === "balanceAssetNames") {
      const names: string[] = [];
      for (const o of objects) {
        const v = o[key];
        if (Array.isArray(v)) names.push(...(v as string[]));
      }
      if (names.length) out[key] = names;
      continue;
    }
    let sum = 0;
    let n = 0;
    for (const o of objects) {
      const v = o[key];
      if (typeof v === "number" && !Number.isNaN(v)) {
        sum += v;
        n += 1;
      }
    }
    if (n > 0) out[key] = sum;
  }
  return out as Json;
}

/**
 * One row per calendar day: for each user, keep the latest snapshot that day, then
 * sum assets/debts across family members. Aligns with RLS-visible rows (self + family).
 */
export const combineFamilyProductBalanceHistoryByDay = (
  rows: ProductBalanceHistoryInput[]
): ProductBalanceHistory[] => {
  if (!rows.length) return [];

  // Latest row per (calendar day, user) so we never double-count re-saved same-day rows.
  const byDayAndUser = new Map<string, ProductBalanceHistoryInput>();
  for (const row of rows) {
    const dk = calendarDayKey(row.created_at);
    const userKey = row.user_id ?? "";
    const composite = `${dk}|${userKey}`;
    const existing = byDayAndUser.get(composite);
    if (
      !existing ||
      new Date(row.created_at).getTime() >
        new Date(existing.created_at).getTime()
    ) {
      byDayAndUser.set(composite, row);
    }
  }

  const byDay = new Map<string, ProductBalanceHistoryInput[]>();
  for (const row of byDayAndUser.values()) {
    const key = calendarDayKey(row.created_at);
    const list = byDay.get(key) ?? [];
    list.push(row);
    byDay.set(key, list);
  }

  const combined: ProductBalanceHistory[] = [];
  for (const [, dayRows] of byDay) {
    dayRows.sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    const uniqueDayRows = Array.from(
      new Map(dayRows.map((row) => [snapshotSignature(row), row])).values()
    );
    let assets = 0;
    let debts = 0;
    let liquid = 0;
    let autoImp = 0;
    const fullDataParts: (Json | null)[] = [];
    let minId = uniqueDayRows[0]!.id;
    let lastCreated = uniqueDayRows[0]!.created_at;

    for (const r of uniqueDayRows) {
      assets += r.assets ?? 0;
      debts += r.debts ?? 0;
      liquid += r.liquid_assets ?? 0;
      autoImp += r.auto_imported_assets ?? 0;
      if (r.id < minId) minId = r.id;
      fullDataParts.push(r.full_data);
      if (new Date(r.created_at).getTime() > new Date(lastCreated).getTime()) {
        lastCreated = r.created_at;
      }
    }

    combined.push({
      id: minId,
      created_at: lastCreated,
      assets: Math.floor(assets),
      debts: Math.floor(debts),
      liquid_assets: Math.floor(liquid),
      auto_imported_assets: Math.floor(autoImp),
      user_id: null,
      full_data: mergeFullDataSnapshots(fullDataParts),
    });
  }

  return combined.sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
};

export const createProductBalanceHistory = async (
    supabase: SupabaseClient<Database>,
    productBalanceHistory: InsertProductBalanceHistory 
) => {
    const { data, error } = await supabase
        .from("product_balance_history")
        .upsert(productBalanceHistory)
        .select("*");
    if (error?.message || !data)
        throw new Error(error?.message || "Cannot save product_balance_history member");

    return data;
}


/**
 * Fetch a single user's product_balance_history by user_id (admin/service-role).
 * Unlike `fetchHistoricData`, this does not go through
 * `get_product_balance_history_daily()` (which filters on auth.uid() and
 * returns an empty set for the Firebase cron's service-role JWT).
 */
export const fetchHistoricDataForUser = async (
    supabase: SupabaseClient<Database>,
    userId: string,
    since?: Date,
) => {
    let query = supabase
        .from("product_balance_history")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: true });
    if (since) {
        query = query.gte("created_at", since.toISOString());
    }
    const { data, error } = await query;
    if (error?.message) {
        throw new Error(error.message || "Cannot fetch product_balance_history for user");
    }
    return (data ?? []) as ProductBalanceHistory[];
};

export const fetchHistoricData = async (
    supabase: SupabaseClient<Database>,
    userId: string
) => {
    const { data: dailyData, error: dailyError } = await (supabase as SupabaseProductBalanceHistoryRpc)
        .rpc("get_product_balance_history_daily");

    if (!dailyError && dailyData) return dailyData;

    const isMissingRpc = dailyError?.message?.includes("get_product_balance_history_daily");
    if (dailyError && !isMissingRpc) {
        throw new Error(dailyError.message || "Cannot fetch product_balance_history daily history");
    }

    const { data: familyUserIds, error: familyError } = await supabase.rpc("get_family_users", {
        identifier: userId,
    });

    if (familyError?.message || !familyUserIds?.length) {
        throw new Error(familyError?.message || "Cannot fetch family members for product_balance_history");
    }

    const { data, error } = await supabase
        .from("product_balance_history")
        .select("*")
        .in("user_id", familyUserIds)
        .order("created_at", { ascending: true });
    if (error?.message || !data)
        throw new Error(error?.message || "Cannot fetch product_balance_history member");
    return combineFamilyProductBalanceHistoryByDay(data);
}

export const updateProductBalanceHistory = async (
    supabase: SupabaseClient<Database>,
    userId: string,
    id: number,
    updates: Partial<Database["public"]["Tables"]["product_balance_history"]["Update"]>
) => {
    const { data, error } = await supabase
        .from("product_balance_history")
        .update(updates)
        .eq("id", id)
        .eq("user_id", userId)
        .select("*");
    if (error?.message)
        throw new Error(error?.message);
    return data;
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    }).formatToParts(date);

    const values = Object.fromEntries(
        parts
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, Number(part.value)])
    );
    const zonedAsUtc = Date.UTC(
        values.year,
        values.month - 1,
        values.day,
        values.hour,
        values.minute,
        values.second
    );

    return zonedAsUtc - date.getTime();
}

function zonedDayRangeUtc(iso: string, timeZone = "Asia/Jerusalem") {
    const targetParts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date(iso));
    const values = Object.fromEntries(
        targetParts
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, Number(part.value)])
    );

    const localMidnightUtcMs = Date.UTC(values.year, values.month - 1, values.day);
    const startEstimate = new Date(localMidnightUtcMs);
    const start = new Date(
        localMidnightUtcMs - getTimeZoneOffsetMs(startEstimate, timeZone)
    );
    const correctedStart = new Date(
        localMidnightUtcMs - getTimeZoneOffsetMs(start, timeZone)
    );

    const nextLocalMidnightUtcMs = Date.UTC(values.year, values.month - 1, values.day + 1);
    const endEstimate = new Date(nextLocalMidnightUtcMs);
    const end = new Date(
        nextLocalMidnightUtcMs - getTimeZoneOffsetMs(endEstimate, timeZone)
    );
    const correctedEnd = new Date(
        nextLocalMidnightUtcMs - getTimeZoneOffsetMs(end, timeZone)
    );

    return { start: correctedStart.toISOString(), end: correctedEnd.toISOString() };
}

export const deleteProductBalanceHistory = async (
    supabase: SupabaseClient<Database>,
    supabaseAdmin: SupabaseClient<Database>,
    userId: string,
    id: number,
) => {
    const { data: familyUserIds, error: familyError } = await supabase.rpc("get_family_users", {
        identifier: userId,
    });

    if (familyError?.message || !familyUserIds?.length) {
        throw new Error(familyError?.message || "Cannot fetch family members for product_balance_history");
    }

    const { data: targetRows, error: targetError } = await supabaseAdmin
        .from("product_balance_history")
        .select("created_at, user_id")
        .eq("id", id)
        .limit(1);

    if (targetError?.message) {
        throw new Error(targetError.message);
    }

    const targetRow = targetRows?.[0];
    if (!targetRow) {
        throw new Error("Product balance history row not found");
    }
    if (!targetRow.user_id || !familyUserIds.includes(targetRow.user_id)) {
        throw new Error("Cannot delete product_balance_history outside the user's family");
    }

    const { start, end } = zonedDayRangeUtc(targetRow.created_at);
    const { data, error } = await supabaseAdmin
        .from("product_balance_history")
        .delete()
        .in("user_id", familyUserIds)
        .gte("created_at", start)
        .lt("created_at", end)
        .select("id");
    if (error?.message)
        throw new Error(error?.message);
    if (!data?.length)
        throw new Error("No product_balance_history rows were deleted");
    return data;
}